import useFcmRegistration from "./hooks/useFcmRegistration";
import HandoverPickupProvider from "./components/Home-page-components/Calls/HandoverPickupProvider";
import { Alert, Button } from "@mui/material";
import React, { useEffect, useMemo, useState, lazy, Suspense } from "react";
import { BrowserRouter, Switch, Route } from "react-router-dom";
import AuthApi from "./components/AuthApi";
import Cookies from "js-cookie";
import axios from "axios";
import CenterLoader from "./utils/CenterLoader";
import WorkflowV2 from "./components/WorkflowV2";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { ToastContainer } from "react-toastify";
import LandingPage from "./components/LandingPage";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfService from "./components/TermsOfService";

const Home = lazy(() => import("./components/Home"));
const Login = lazy(() => import("./components/Login"));
const ProtectedLogin = lazy(() => import("./components/ProtectedLogin"));
const ProtectedRoute = lazy(() => import("./components/ProtectedRoute"));
const Contact = lazy(() => import("./components/Home-page-components/Contact"));
const ContactProfile = lazy(() =>
  import("./components/Home-page-components/ContactListing/ContactProfile")
);
const Campaigns = lazy(() =>
  import("./components/Home-page-components/Campaigns/Campaigns")
);
const Candidates = lazy(() =>
  import("./components/Home-page-components/Candidates")
);
const Communicate = lazy(() =>
  import("./components/Home-page-components/Communicate/Communicate")
);
const Analytics = lazy(() =>
  import("./components/Home-page-components/Analytics")
);
const AddForm = lazy(() => import("./components/Home-page-components/AddForm"));
const Listing = lazy(() => import("./components/Home-page-components/Listing"));
const Settings = lazy(() =>
  import("./components/Home-page-components/Settings")
);
const Dashboard = lazy(() =>
  import("./components/Home-page-components/Dashboard")
);
const AddCandidate = lazy(() =>
  import("./components/Home-page-components/AddCandidate")
);
const CandidateByID = lazy(() =>
  import("./components/Home-page-components/CandidateByID")
);
const AddCampaign = lazy(() =>
  import("./components/Home-page-components/Campaigns/AddCampaign")
);
const Calls = lazy(() =>
  import("./components/Home-page-components/Calls/Calls")
);
const CallDetailPage = lazy(() =>
  import("./components/Home-page-components/Calls/CallDetailPage")
);
const Sidebar = lazy(() => import("./components/utils/components/Sidebar"));
const WorkFlow = lazy(() => import("./components/WorkFlow/WorkFlow"));
const App1 = lazy(() => import("./components/WorkFlow/App"));
const ForgotPassword = lazy(() => import("./components/ForgotPassword"));
const ResetPassword = lazy(() => import("./components/ResetPassword"));
const SalesCRM = lazy(() =>
  import("./components/Home-page-components/SalesCRM/SalesCRM")
);
const Alerts = lazy(() =>
  import("./components/Home-page-components/Alerts/Alerts")
);
const SocialApp = lazy(() => import("./components/Social/SocialApp"));
const OutreachApp = lazy(() => import("./components/Outreach/OutreachApp"));


function ThemedToastContainer() {
  const { darkMode } = useTheme();
  return (
    <ToastContainer
      position="top-center"
      autoClose={3000}
      hideProgressBar={false}
      newestOnTop={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme={darkMode ? "dark" : "light"}
    />
  );
}

const NoModuleAccess = () => (
  <div className="dashboard">
    <div className="search-container">
      <div className="data-container">
        <h1 className="text-2xl font-bold">No module access</h1>
        <p className="mt-2 text-gray-500">
          Ask an administrator to enable at least one module for your role.
        </p>
      </div>
    </div>
  </div>
);

function App() {
  const [auth, setAuth] = useState(() => !!Cookies.get("auth_token"));
  const pushRegistration = useFcmRegistration(auth);
  const [toggleSlider, setToggleSlider] = useState(true);
  const authContextValue = useMemo(
    () => ({ auth, setAuth, toggleSlider, setToggleSlider }),
    [auth, toggleSlider]
  );

  function readCookie() {
    const currentUser = Cookies.get("auth_token");
    if (currentUser) {
      setAuth(true);
    }
  }

  useEffect(() => {
    readCookie();
  }, []);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          setAuth(false);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  useEffect(() => {
    if (auth && window.location.pathname === "/") {
      window.location.href = "/outreach";
    }
  }, [auth]);

  return (
    <ThemeProvider>
      <AuthApi.Provider value={authContextValue}>
        <div className="App">
          <ThemedToastContainer />
          <BrowserRouter>
            <Suspense fallback={<CenterLoader />}>
              <Switch>
                <Route exact path="/" component={LandingPage} />
                <Route exact path={["/privacy", "/privacy.html"]} component={PrivacyPolicy} />
                <Route exact path={["/terms", "/terms.html"]} component={TermsOfService} />
                <ProtectedLogin path="/Login" auth={auth} component={Login} />
                <Route path="/forgotpassword" component={ForgotPassword} />
                <Route path="/resetPassword" component={ResetPassword} />
              </Switch>
              {auth && !window.location.pathname.toLowerCase().startsWith("/privacy") && !window.location.pathname.toLowerCase().startsWith("/terms") && <Sidebar />}
              {auth && !window.location.pathname.toLowerCase().startsWith("/privacy") && !window.location.pathname.toLowerCase().startsWith("/terms") && pushRegistration.permission === "default" && <Alert action={<Button onClick={pushRegistration.enable}>Enable notifications</Button>}>Enable background call notifications.</Alert>}
              {auth && !window.location.pathname.toLowerCase().startsWith("/privacy") && !window.location.pathname.toLowerCase().startsWith("/terms") && <HandoverPickupProvider />}
              <Switch>
                <ProtectedRoute path="/outreach" auth={auth} setAuth={setAuth} moduleId="campaigns" component={OutreachApp} />
                <ProtectedRoute
                  path="/workflow"
                  auth={auth}
                  setAuth={setAuth}
                  moduleId="workflow"
                  component={WorkFlow}
                />
                <ProtectedRoute
                  path="/workflow-editor/:id"
                  auth={auth}
                  setAuth={setAuth}
                  moduleId="workflow"
                  component={WorkflowV2}
                />
                <ProtectedRoute
                  path="/workflow-editor"
                  auth={auth}
                  setAuth={setAuth}
                  moduleId="workflow"
                  component={WorkflowV2}
                />
                <ProtectedRoute
                  path="/Home"
                  auth={auth}
                  setAuth={setAuth}
                  component={Home}
                />
                <ProtectedRoute
                  path="/wf"
                  auth={auth}
                  setAuth={setAuth}
                  moduleId="workflow"
                  component={App1}
                />
                <ProtectedRoute
                  path={["/Dashboard", "/dashboard"]}
                  auth={auth}
                  setAuth={setAuth}
                  moduleId="dashboard"
                  component={Dashboard}
                />
                <ProtectedRoute
                  path="/Contacts/:id"
                  auth={auth}
                  setAuth={setAuth}
                  moduleId="profile"
                  component={ContactProfile}
                />
                <ProtectedRoute
                  path="/Contact"
                  auth={auth}
                  setAuth={setAuth}
                  moduleId="profile"
                  component={Contact}
                />
                <ProtectedRoute
                  path="/Campaigns"
                  auth={auth}
                  setAuth={setAuth}
                  moduleId="campaigns"
                  component={Campaigns}
                />
                <ProtectedRoute
                  path="/addcampaign"
                  auth={auth}
                  setAuth={setAuth}
                  moduleId="campaigns"
                  component={AddCampaign}
                />
                <ProtectedRoute
                  path="/Candidates"
                  auth={auth}
                  setAuth={setAuth}
                  component={Candidates}
                />
                <ProtectedRoute
                  path={["/Communicate", "/communicate"]}
                  auth={auth}
                  setAuth={setAuth}
                  moduleId="communicate"
                  component={Communicate}
                />
                <ProtectedRoute
                  path={["/Calls/:id", "/calls/:id"]}
                  auth={auth}
                  setAuth={setAuth}
                  moduleId="calls"
                  component={CallDetailPage}
                />
                <ProtectedRoute
                  path={["/Calls", "/calls"]}
                  auth={auth}
                  setAuth={setAuth}
                  moduleId="calls"
                  component={Calls}
                />
                <ProtectedRoute
                  path={["/SalesCRM", "/sales-crm"]}
                  auth={auth}
                  setAuth={setAuth}
                  moduleId="sales-crm"
                  component={SalesCRM}
                />
                <ProtectedRoute
                  path={["/Alerts", "/alerts"]}
                  auth={auth}
                  setAuth={setAuth}
                  moduleId="alerts"
                  component={Alerts}
                />
                <ProtectedRoute
                  path={["/Social", "/social"]}
                  auth={auth}
                  setAuth={setAuth}
                  moduleId="social"
                  component={SocialApp}
                />
                <ProtectedRoute
                  path="/Analytics"
                  auth={auth}
                  setAuth={setAuth}
                  moduleId="analytics"
                  component={Analytics}
                />
                <ProtectedRoute
                  path="/AddForm"
                  auth={auth}
                  setAuth={setAuth}
                  component={AddForm}
                />
                <ProtectedRoute
                  path="/Listing/:id"
                  auth={auth}
                  setAuth={setAuth}
                  component={Listing}
                />
                <ProtectedRoute
                  path="/Settings"
                  auth={auth}
                  setAuth={setAuth}
                  moduleId="settings"
                  component={Settings}
                />
                <ProtectedRoute
                  path="/no-module-access"
                  auth={auth}
                  setAuth={setAuth}
                  component={NoModuleAccess}
                />
                <ProtectedRoute
                  path="/AddCandidate"
                  auth={auth}
                  setAuth={setAuth}
                  component={AddCandidate}
                />
                <ProtectedRoute
                  path="/Foo"
                  auth={auth}
                  setAuth={setAuth}
                  component={CandidateByID}
                />
              </Switch>
            </Suspense>
          </BrowserRouter>
        </div>
      </AuthApi.Provider>
    </ThemeProvider>
  );
}

export default App;
